import * as React from 'react';

import {
  COLOR_SCALE,
  SIZE_SCALE,
  PADDING_X,
  PADDING_Y,
  MARGIN_BOTTOM,
  MARGIN_TOP,
  MARGIN_LEFT,
  POSITION_SCALE,
  RADIUS_SCALE,
  TYPE_SCALE,
  FONT_WEIGHTS,
  BORDER_WIDTH,
} from '@/components/design-system/tokens';
import { cn } from '@/lib/utils';

interface NumberFieldProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'size'
> {
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  error?: boolean;
  helperText?: string;
  label?: string;
  placeholder?: string;
  value?: string | number;
  defaultValue?: string | number;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onValueChange?: (value: string) => void;
  min?: number;
  max?: number;
  step?: number;
  required?: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
  showSpinners?: boolean;
}

// Enhanced NumberField with spinner controls (like the official MUI example)

const NumberField = React.forwardRef<HTMLInputElement, NumberFieldProps>(
  (
    {
      className,
      size = 'medium',
      fullWidth = true,
      error = false,
      helperText,
      label,
      placeholder,
      value,
      defaultValue,
      onChange,
      onValueChange, // For backward compatibility with onCheckedChange pattern
      min,
      max,
      step = 1,
      required = false,
      disabled = false,
      hideLabel = false, // Hide MUI label when using separate Label components
      showSpinners = false, // Option to show increment/decrement spinners
      ...props
    },
    ref
  ) => {
    // Handle both onChange and custom onValueChange for compatibility
    const handleChange = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        if (onValueChange) {
          onValueChange(event.target.value);
        }
        if (onChange) {
          onChange(event);
        }
      },
      [onValueChange, onChange]
    );

    // Handle spinner button clicks
    const handleIncrement = React.useCallback(() => {
      const currentValue = typeof value === 'string' ? Number.parseFloat(value) : (value ?? 0);
      const newValue = Math.min(currentValue + step, max ?? Infinity);
      const syntheticEvent = {
        target: { value: newValue.toString() },
      } as React.ChangeEvent<HTMLInputElement>;
      handleChange(syntheticEvent);
    }, [value, step, max, handleChange]);

    const handleDecrement = React.useCallback(() => {
      const currentValue = typeof value === 'string' ? Number.parseFloat(value) : (value ?? 0);
      const newValue = Math.max(currentValue - step, min ?? -Infinity);
      const syntheticEvent = {
        target: { value: newValue.toString() },
      } as React.ChangeEvent<HTMLInputElement>;
      handleChange(syntheticEvent);
    }, [value, step, min, handleChange]);

    // Helper function to compute size classes
    const getSizeClasses = () => {
      switch (size) {
        case 'small':
          return `${SIZE_SCALE['8']} ${PADDING_X['2']} ${TYPE_SCALE.xs}`;
        case 'large':
          return `${SIZE_SCALE['12']} ${PADDING_X['4']} ${TYPE_SCALE.base}`;
        default:
          return '';
      }
    };

    // Base input styles matching our design system
    const baseClasses = `
    flex ${SIZE_SCALE['10']} w-full ${RADIUS_SCALE.lg} ${COLOR_SCALE['number-field-border']} ${COLOR_SCALE['number-field-bg']} ${PADDING_X['3']} ${PADDING_Y['2']} ${TYPE_SCALE.sm}
    ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium
    ${COLOR_SCALE['number-field-placeholder']} focus-visible:outline-none focus-visible:ring-2
    ${COLOR_SCALE['number-field-focus']} focus-visible:ring-offset-2 disabled:cursor-not-allowed
    disabled:opacity-50
  `;

    const errorClasses = error ? COLOR_SCALE['number-field-error'] : '';
    const sizeClasses = getSizeClasses();

    return (
      <div
        className={cn(`${POSITION_SCALE.relative}`, fullWidth ? `${SIZE_SCALE.full}` : 'w-auto')}
      >
        {/* Label (only show if not hidden) */}
        {label && !hideLabel && (
          <label
            className={`${TYPE_SCALE.sm} ${FONT_WEIGHTS.medium} ${COLOR_SCALE['number-field-label']} ${MARGIN_BOTTOM['2']} block`}
          >
            {label}
            {required && (
              <span className={`${COLOR_SCALE['number-field-required']} ${MARGIN_LEFT['1']}`}>
                *
              </span>
            )}
          </label>
        )}

        {/* Input with optional spinners */}
        <div className={`${POSITION_SCALE.relative}`}>
          <input
            ref={ref}
            type="number"
            className={cn(
              baseClasses,
              errorClasses,
              sizeClasses,
              showSpinners ? `${POSITION_SCALE.right['6']}` : '', // Add padding for spinner buttons
              className
            )}
            value={value}
            defaultValue={defaultValue}
            onChange={handleChange}
            placeholder={placeholder}
            min={min}
            max={max}
            step={step}
            required={required}
            disabled={disabled}
            {...props}
          />

          {/* Spinner buttons */}
          {showSpinners && (
            <div
              className={`absolute ${POSITION_SCALE.right['0']} ${POSITION_SCALE.top['0']} bottom-0 flex flex-col ${BORDER_WIDTH.thin}-l ${COLOR_SCALE['number-field-spinner-border']}`}
            >
              <button
                type="button"
                onClick={handleIncrement}
                disabled={
                  disabled || (max !== undefined && Number.parseFloat(String(value)) >= max)
                }
                className={`flex-1 ${PADDING_X['2']} ${COLOR_SCALE['number-field-spinner-hover']} disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center`}
              >
                <svg
                  className={`${SIZE_SCALE.w3} ${SIZE_SCALE['3']}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 15l7-7 7 7"
                  />
                </svg>
              </button>
              <button
                type="button"
                onClick={handleDecrement}
                disabled={
                  disabled || (min !== undefined && Number.parseFloat(String(value)) <= min)
                }
                className={`flex-1 ${PADDING_X['2']} ${COLOR_SCALE['number-field-spinner-hover']} disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${BORDER_WIDTH.thin}-t ${COLOR_SCALE['number-field-spinner-border']}`}
              >
                <svg
                  className={`${SIZE_SCALE.w3} ${SIZE_SCALE['3']}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Helper text */}
        {helperText && (
          <p
            className={cn(
              `${TYPE_SCALE.xs} ${MARGIN_TOP['1']}`,
              error
                ? COLOR_SCALE['number-field-error-text']
                : COLOR_SCALE['number-field-helper-text']
            )}
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

NumberField.displayName = 'NumberField';

export { NumberField };
